import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileDown, FileSpreadsheet, Info, Upload } from 'lucide-react';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useAuth } from '../../auth/AuthProvider';
import { api, errorText, openFile, unwrap, uploadForm } from '../../api/client';
import type { Reconciliation, ReconciliationLine, ReconciliationListItem } from '../../api/types';
import { AssetLink } from '../../components/assets/AssetLink';
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  EmptyState,
  ErrorBox,
  Field,
  Input,
  Loading,
  PageHeader,
  Pagination,
  Table,
  Td,
  Th,
  Tr,
  useToast,
} from '../../components/ui';
import { formatDate, formatDateTime, formatMoney } from '../../lib/format';

/**
 * Inventarabgleich: Inventarverzeichnis der Buchhaltung (Excel) hochladen → neue Excel mit
 * verschrotteten Geräten (grau), neuen Geräten unter „noch ungeordnet“ und unbekannten Anlagennummern.
 * Optional werden die Werte gefundener Geräte (Steuerwerte, leere Felder Kosten/Aktivierung) ins Tool übernommen.
 */

const download = (r: Pick<ReconciliationListItem, 'result_url' | 'result_filename' | 'source_url' | 'source_filename'>, which: 'result' | 'source') =>
  which === 'result' ? openFile(r.result_url, { download: r.result_filename }) : openFile(r.source_url, { download: r.source_filename });

function ResultBadges({ stats, values }: { stats: ReconciliationListItem['stats']; values: ReconciliationListItem['values_import'] }) {
  return (
    <span className="flex flex-wrap gap-1">
      <Badge>{stats.disposed_marked} verschrottet</Badge>
      <Badge tone={stats.new_assets ? 'blue' : 'gray'}>{stats.new_assets} neu</Badge>
      <Badge tone={stats.not_found ? 'amber' : 'gray'}>{stats.not_found} nicht gefunden</Badge>
      {values && <Badge tone="green">{values.assets_changed} Werte übernommen</Badge>}
    </span>
  );
}

export function ReconciliationListPage() {
  const qc = useQueryClient();
  const toast = useToast();
  const navigate = useNavigate();
  const [file, setFile] = useState<File | null>(null);
  const [minCost, setMinCost] = useState('250');
  // Jahresabschluss wird für das Vorjahr gemacht
  const [year, setYear] = useState(String(new Date().getFullYear() - 1));
  const [applyValues, setApplyValues] = useState(false);
  const { can } = useAuth();
  const canApply = can('asset:write');
  const [page, setPage] = useState(1);
  const pageSize = 20;

  const history = useQuery({
    queryKey: ['reconciliations', page],
    queryFn: () => unwrap(api.GET('/api/inventory-reconciliations', { params: { query: { page, page_size: pageSize } } })),
    placeholderData: keepPreviousData,
  });

  const run = useMutation({
    mutationFn: () => {
      const form = new FormData();
      form.append('file', file!);
      const v = Number(minCost.replace(',', '.'));
      if (Number.isFinite(v) && v >= 0) form.append('min_cost_eur', String(v));
      if (/^\d{4}$/.test(year)) form.append('fiscal_year', year);
      if (applyValues && canApply) form.append('apply_values', 'true');
      return uploadForm<Reconciliation>('/api/inventory-reconciliations', form);
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ['reconciliations'] });
      toast(r.values_import ? `Abgleich erstellt – bei ${r.values_import.assets_changed} Geräten Werte übernommen` : 'Abgleich erstellt');
      navigate(`/reconciliations/${r.id}`);
    },
  });

  return (
    <>
      <PageHeader title="Inventarabgleich" subtitle="Inventarverzeichnis der Buchhaltung mit dem IT-Inventar abgleichen" />

      <div className="mb-5 grid gap-5 lg:grid-cols-5">
        <Card className="lg:col-span-3">
          <CardHeader title="Neuer Abgleich" />
          <CardBody>
            <form
              className="space-y-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (file) run.mutate();
              }}
            >
              <Field label="Inventarverzeichnis (.xlsx)" required>
                {(p) => (
                  <input
                    {...p}
                    type="file"
                    accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                    className="block w-full text-sm file:mr-3 file:rounded-md file:border-0 file:bg-brand-50 file:px-3 file:py-1.5 file:text-sm file:font-medium file:text-brand-700 hover:file:bg-brand-100"
                  />
                )}
              </Field>
              <Field
                label="Wirtschaftsjahr"
                hint="Neue Geräte nur mit Anschaffung in diesem Jahr oder ohne Datum. Später gekaufte Geräte werden ignoriert."
                className="max-w-md"
              >
                {(p) => (
                  <Input {...p} inputMode="numeric" maxLength={4} value={year} onChange={(e) => setYear(e.target.value.replace(/\D/g, ''))} className="max-w-[8rem]" />
                )}
              </Field>
              <Field
                label="Mindestwert für neue Geräte (€ netto)"
                hint="Günstigere Geräte (z. B. Maus, Headset) kommen nicht in die Liste. Geräte ohne Preis im Tool kommen immer rein."
                className="max-w-md"
              >
                {(p) => <Input {...p} inputMode="decimal" value={minCost} onChange={(e) => setMinCost(e.target.value)} className="max-w-xs" />}
              </Field>
              {canApply && (
                <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
                  <Checkbox
                    label={<strong>Werte ins Tool übernehmen</strong>}
                    checked={applyValues}
                    onChange={(e) => setApplyValues(e.target.checked)}
                  />
                  <p className="mt-1 pl-6 text-xs text-slate-500">
                    Nur für gefundene, nicht verschrottete Geräte – es werden nur <strong>leere Felder ergänzt, nichts überschrieben</strong>:
                    Steuerwerte des Wirtschaftsjahres (AHK, AHK-Datum, ND, AfA), Kosten netto, Kosten brutto (netto + 19 % USt.) und
                    Aktivierungsdatum. Stehen mehrere Geräte unter einer Anlagennummer, behalten Geräte mit Preis im Tool ihren Preis als Anteil;
                    der Rest wird auf die Geräte ohne Preis verteilt, dazu eine Notiz am Gerät. Jede Änderung steht in der Historie des Assets.
                  </p>
                </div>
              )}
              <ErrorBox error={run.error} />
              <Button type="submit" variant="primary" icon={<Upload className="size-4" />} loading={run.isPending} disabled={!file}>
                Abgleichen
              </Button>
            </form>
          </CardBody>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader title="Was passiert?" />
          <CardBody className="space-y-2 text-sm text-slate-600">
            <p>Jede Zeile wird über die <strong>Anlagennummer</strong> mit den Assets verglichen (Feld „Anlagennummer Buchhaltung“ oder Steuerwerte).</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                <strong>Verschrottete Geräte</strong>: Zeile grau, Hinweis mit Datum und Vorgang in der Notizspalte.
              </li>
              <li>
                <strong>Neue Geräte</strong> ohne Anlagennummer, angeschafft im gewählten Jahr (oder ohne Datum), ab Mindestwert – und alle ohne
                Preis im Tool (Hinweis „Preis fehlt“): unten unter „noch ungeordnet“ mit Nummer <code>xxxxxx</code>. Später angeschaffte Geräte
                werden ignoriert.
              </li>
              <li>
                <strong>Unbekannte Anlagennummern</strong>: eigene Liste darunter zum Prüfen.
              </li>
            </ul>
            <p className="flex items-start gap-1.5 text-slate-500">
              <Info className="mt-0.5 size-4 shrink-0" /> Du bekommst eine neue Excel-Datei; das Original bleibt unangetastet. Die Assets im Tool
              ändern sich nur, wenn du „Werte ins Tool übernehmen“ anhakst.
            </p>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader title="Letzte Abgleiche" />
        {history.isPending ? (
          <Loading />
        ) : history.error ? (
          <ErrorBox error={history.error} className="m-4" />
        ) : history.data.data.length === 0 ? (
          <EmptyState title="Noch keine Abgleiche" icon={<FileSpreadsheet className="size-10" />} />
        ) : (
          <>
            <Table>
              <thead>
                <tr>
                  <Th>Datum</Th>
                  <Th>Von</Th>
                  <Th>Datei</Th>
                  <Th>Ergebnis</Th>
                  <Th />
                </tr>
              </thead>
              <tbody>
                {history.data.data.map((r) => (
                  <Tr key={r.id} onClick={() => navigate(`/reconciliations/${r.id}`)}>
                    <Td className="whitespace-nowrap">{formatDateTime(r.created_at)}</Td>
                    <Td>{r.created_by?.display_name ?? '–'}</Td>
                    <Td>
                      <div className="font-medium text-brand-700">{r.source_filename}</div>
                      {r.sheet_title && <div className="text-xs text-slate-500">{r.sheet_title}</div>}
                      {r.fiscal_year && <div className="text-xs text-slate-500">Wirtschaftsjahr {r.fiscal_year}</div>}
                    </Td>
                    <Td>
                      <ResultBadges stats={r.stats} values={r.values_import} />
                    </Td>
                    <Td className="text-right" onClick={(e) => e.stopPropagation()}>
                      <Button
                        size="sm"
                        icon={<FileDown className="size-4" />}
                        onClick={() => download(r, 'result').catch((e: unknown) => toast(errorText(e), 'error'))}
                      >
                        Ergebnis
                      </Button>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
            <Pagination page={page} pageSize={pageSize} total={history.data.total} onPage={setPage} />
          </>
        )}
      </Card>
    </>
  );
}

const SECTIONS: Array<{ kind: ReconciliationLine['kind']; title: string; description: string }> = [
  { kind: 'disposed', title: 'Verschrottet – grau markiert', description: 'Im Tool verschrottet; in der Excel grau hinterlegt mit Hinweis in der Notizspalte.' },
  { kind: 'partially_disposed', title: 'Teilweise verschrottet', description: 'Mehrere Geräte unter einer Anlagennummer, nur ein Teil verschrottet – nur Hinweis, nicht grau.' },
  { kind: 'new', title: 'Neu – „noch ungeordnet“', description: 'Unten in der Excel mit Anlagennummer xxxxxx. Die Buchhaltung vergibt die Nummer; danach im Asset unter „Anlagennummer Buchhaltung“ eintragen.' },
  { kind: 'not_found', title: 'Anlagennummer nicht im Verzeichnis', description: 'Im Tool hinterlegte Nummer kommt im Verzeichnis nicht vor – Tippfehler oder fehlt in der Buchhaltung?' },
  { kind: 'values_imported', title: 'Werte ins Tool übernommen', description: 'Nur leere Felder ergänzt: Steuerwerte des Wirtschaftsjahres, Kosten netto, Kosten brutto (19 % USt.), Aktivierungsdatum. Bei gemeinsamer Anlagennummer nach Preis im Tool bzw. Rest gleichmäßig.' },
  { kind: 'values_skipped', title: 'Werte nicht übernommen', description: 'Bitte von Hand prüfen – der Grund steht im Hinweis.' },
];

const MONEY_KINDS: Array<ReconciliationLine['kind']> = ['new', 'not_found', 'values_imported', 'values_skipped'];

export function ReconciliationDetailPage() {
  const { id = '' } = useParams();
  const toast = useToast();
  const q = useQuery({
    queryKey: ['reconciliations', 'detail', id],
    queryFn: () => unwrap(api.GET('/api/inventory-reconciliations/{id}', { params: { path: { id } } })),
  });

  if (q.isPending) return <Loading />;
  if (q.error) return <ErrorBox error={q.error} />;
  const r = q.data;
  const s = r.stats;
  const tiles: Array<[string, number | string, string?]> = [
    ['Zeilen im Verzeichnis', s.inventory_rows, `${s.matched_rows} im IT-Inventar gefunden`],
    ['Verschrottet markiert', s.disposed_marked, s.partially_disposed ? `+ ${s.partially_disposed} teilweise` : undefined],
    ['Neu (noch ungeordnet)', s.new_assets, `${formatMoney(s.new_assets_cost_cents)}${s.new_assets_without_cost ? ` · ${s.new_assets_without_cost} ohne Preis` : ''}`],
    ['Nummer nicht gefunden', s.not_found],
  ];

  return (
    <>
      <PageHeader
        back={
          <Link to="/reconciliations" className="inline-flex items-center gap-1 text-slate-500 hover:text-slate-800">
            <ArrowLeft className="size-4" /> Inventarabgleich
          </Link>
        }
        title={r.source_filename}
        subtitle={
          <>
            {r.sheet_title && <span>{r.sheet_title} · </span>}
            {r.fiscal_year && <span>Wirtschaftsjahr {r.fiscal_year} · </span>}
            {formatDateTime(r.created_at)} von {r.created_by?.display_name ?? '–'} · Mindestwert {formatMoney(r.min_cost_cents)}
          </>
        }
        actions={
          <>
            <Button icon={<FileDown className="size-4" />} onClick={() => download(r, 'source').catch((e: unknown) => toast(errorText(e), 'error'))}>
              Original
            </Button>
            <Button variant="primary" icon={<FileDown className="size-4" />} onClick={() => download(r, 'result').catch((e: unknown) => toast(errorText(e), 'error'))}>
              Ergebnis-Excel
            </Button>
          </>
        }
      />

      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map(([label, value, hint]) => (
          <div key={label} className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs font-medium text-slate-500">{label}</div>
            <div className="mt-1 text-2xl font-semibold">{value}</div>
            {hint && <div className="text-xs text-slate-400">{hint}</div>}
          </div>
        ))}
      </div>

      {r.values_import && (
        <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900">
          <strong>Werte ins Tool übernommen (Wirtschaftsjahr {r.values_import.fiscal_year_label}):</strong> {r.values_import.assets_changed} Geräte geändert ·{' '}
          {r.values_import.tax_records_created} Steuerwerte angelegt · {r.values_import.tax_records_updated} aktualisiert · {r.values_import.costs_filled} × Kosten
          netto, {r.values_import.gross_costs_filled} × Kosten brutto und {r.values_import.capitalization_dates_filled} × Aktivierungsdatum eingetragen
          {r.values_import.shared_numbers > 0 && <> · {r.values_import.shared_numbers} gemeinsame Anlagennummern aufgeteilt</>}
          {r.values_import.skipped > 0 && <> · {r.values_import.skipped} nicht übernommen</>}
        </div>
      )}

      {s.skipped_below_min_cost + s.skipped_without_cost + s.skipped_components + s.skipped_before_year + s.skipped_after_year + s.skipped_excluded > 0 && (
        <p className="mb-5 text-sm text-slate-500">
          Nicht in der Liste (ohne Anlagennummer): {s.skipped_below_min_cost} unter Mindestwert
          {s.skipped_without_cost > 0 && <>, {s.skipped_without_cost} ohne Kosten</>}, {s.skipped_components} Komponenten bzw. eingebaute Teile
          {r.fiscal_year && (
            <>
              , {s.skipped_before_year} angeschafft vor {r.fiscal_year}, {s.skipped_after_year} angeschafft nach {r.fiscal_year} (ignoriert)
            </>
          )}
          {s.skipped_excluded > 0 && <>, {s.skipped_excluded} am Gerät ausgenommen (bereits anderweitig abgerechnet)</>}.
        </p>
      )}

      <div className="space-y-5">
        {SECTIONS.map((sec) => {
          const lines = r.lines.filter((l) => l.kind === sec.kind);
          if (!lines.length) return null;
          return (
            <Card key={sec.kind}>
              <CardHeader title={`${sec.title} (${lines.length})`} description={sec.description} />
              <Table>
                <thead>
                  <tr>
                    <Th>Zeile</Th>
                    <Th>Anlagennr.</Th>
                    <Th>Bezeichnung</Th>
                    <Th>IT-Nr.</Th>
                    <Th>Datum</Th>
                    {MONEY_KINDS.includes(sec.kind) && <Th className="text-right">{sec.kind.startsWith('values') ? 'AHK' : 'Netto'}</Th>}
                    <Th>Hinweis</Th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((l, i) => (
                    <Tr key={`${l.kind}-${l.sheet_row}-${i}`}>
                      <Td className="text-slate-500">{l.sheet_row}</Td>
                      <Td className="font-mono">{l.inventory_number ?? '–'}</Td>
                      <Td>{l.inventory_name ?? '–'}</Td>
                      <Td className="whitespace-nowrap">
                        {l.assets.map((a) => (
                          <div key={a.asset_number}>
                            <AssetLink asset={a} />
                          </div>
                        ))}
                      </Td>
                      <Td className="whitespace-nowrap">{formatDate(l.date)}</Td>
                      {MONEY_KINDS.includes(sec.kind) && <Td className="text-right whitespace-nowrap">{formatMoney(l.cost_net_cents)}</Td>}
                      <Td className="text-xs text-slate-500">{l.note}</Td>
                    </Tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          );
        })}
        {r.lines.length === 0 && <EmptyState title="Keine Abweichungen – Verzeichnis und IT-Inventar passen zusammen." />}
      </div>
    </>
  );
}
